import { AggregateRoot } from '@swoft/core';
import { Task, TaskSchema } from '../view-models';
import { TaskId } from '../value-objects/TaskId';
import { TaskAssigned } from '../events/TaskAssigned';
import { TaskCompleted } from '../events/TaskCompleted';

/**
 * Aggregate root for Task domain, encapsulating business logic and invariants.
 * 
 * Manages task lifecycle, assignment rules, and completion workflow
 * following Eric Evans' DDD principles with event-driven state changes.
 */
export class TaskAggregate extends AggregateRoot<TaskId> {
  constructor(
    id: TaskId,
    private props: Task
  ) {
    super(id);
  }

  /**
   * Reconstruct aggregate from persistence layer.
   */
  static fromDTO(dto: Task): TaskAggregate {
    return new TaskAggregate(
      TaskId.fromString(dto.id),
      dto
    );
  }

  /**
   * Create new task with generated ID.
   */
  static create(props: Omit<Task, 'id'>): TaskAggregate {
    const id = TaskId.generate();
    const task: Task = {
      ...props,
      id: id.toString()
    };
    return new TaskAggregate(id, task);
  }

  /**
   * Assign this task to a developer.
   * 
   * Business Rules:
   * - Only 'new' or 'in-progress' tasks can be assigned
   * - Assignment triggers workflow automation via TaskAssigned event
   */
  assignTo(partyId: string, roleType: string): void {
    if (this.props.status !== 'new' && this.props.status !== 'in-progress') {
      throw new Error(
        `Cannot assign task ${this.props.id} in status ${this.props.status}`
      );
    }
    
    this.apply(new TaskAssigned(
      this.getId().toString(),
      partyId,
      roleType,
      new Date()
    ));
  }

  /**
   * Event handler: Task assignment state change.
   */
  onTaskAssigned(event: TaskAssigned): void {
    this.props.assignedTo = event.partyId;
    this.props.roleType = event.roleType;
    this.props.assignedAt = event.assignedAt.toISOString();
    
    // Auto-transition to in-progress if newly assigned
    if (this.props.status === 'new') {
      this.props.status = 'in-progress';
    }
  }

  /**
   * Mark this task as completed.
   * 
   * Business Rules:
   * - Cannot complete already completed tasks
   * - Cannot complete blocked tasks
   * - Completion triggers analytics and workload rebalancing
   */
  complete(completedBy?: string): void {
    if (this.props.status === 'done') {
      throw new Error(
        `Task ${this.props.id} is already completed`
      );
    }
    if (this.props.status === 'blocked') {
      throw new Error(
        `Cannot complete blocked task ${this.props.id}`
      );
    }
    
    this.apply(new TaskCompleted(
      this.getId().toString(),
      completedBy,
      new Date()
    ));
  }

  /**
   * Event handler: Task completion state change.
   */
  onTaskCompleted(event: TaskCompleted): void {
    this.props.status = 'done';
    this.props.completedAt = event.completedAt.toISOString();
  }

  /**
   * Block this task due to dependencies or issues.
   */
  block(reason: string): void {
    if (this.props.status === 'done') {
      throw new Error(`Cannot block completed task ${this.props.id}`);
    }
    
    this.props.status = 'blocked';
    this.props.blockedReason = reason;
    this.props.blockedAt = new Date().toISOString();
  }

  /**
   * Unblock this task and return to previous state.
   */
  unblock(): void {
    if (this.props.status !== 'blocked') {
      throw new Error(`Task ${this.props.id} is not blocked`);
    }
    
    // Return to in-progress if assigned, otherwise new
    this.props.status = this.props.assignedTo ? 'in-progress' : 'new';
    this.props.blockedReason = undefined;
    this.props.blockedAt = undefined;
  }

  /** Export aggregate state as DTO */
  toDTO(): Task {
    return TaskSchema.parse(this.props);
  }
}
